import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RRLink, LinkProps as RRLinkProps } from 'react-router-dom';
import { Theme, Container, Box, Link, LinkProps, Button, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Routes, useTypedLocation } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as RentalsSelectors from 'selectors/RentalsSelectors';
import { logEvent, newOnboardingClickEvent } from 'common/services/analytics';
import * as IntercomService from 'services/intercom';
import history from 'services/history';
import UpgradeIcon from 'common/assets/upgradePlanIcon.jpg';
import SupportIcon from 'common/assets/supportIcon.jpg';
import KnowledgeIcon from 'common/assets/knowledgeIcon.jpg';
import HomeCTA from './components/HomeCTA';
import TodaySection from './components/TodaySection';
import OnboardingSection from './components/OnboardingSection';
import ExternalLink from 'components/ExternalLink';
import { api } from 'common/frontend/api';
import ProductCreatedModal from './components/ProductCreatedModal';
import { ShopOnlinePaymentMethodObject } from 'common/modules/payments/types';
import { OnboardingSteps } from 'common/services/analytics/tractionAnalytics';
import { uniq } from 'lodash';

const Home = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { state } = useTypedLocation();
	const plan = useSelector(ShopSelectors.shopPlan);
	const bookingsToday = useSelector(RentalsSelectors.upcomingRentalsToday);
	const amountOfBookingsToday = bookingsToday.length;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopStoreUrl = useSelector(ShopSelectors.activeShopOnlineURL);
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const activePaymentMethods = useSelector(ShopSelectors.getActivePaymentMethods);
	const [showProductCreatedModal, setShowProductCreatedModal] = useState(
		state?.from?.includes('/products/new') ?? false,
	);

	const showTodaySection =
		!shopAnalytics?.showOnboarding ||
		OnboardingSteps.every(
			(onboardingStep) =>
				shopAnalytics?.onboardingActions && !!shopAnalytics.onboardingActions[onboardingStep],
		);

	const handleTalkToUsClick = () => {
		IntercomService.open();
		logEvent('chat_with_us_press', { from: history.location.pathname });
	};

	const _ExternalLink = (props: LinkProps) => (
		<Link {...props} target="_blank" rel="noopener noreferrer" />
	);

	const ChangePlanLink = (props: RRLinkProps) => (
		<RRLink to={props.href!}>
			<Button variant="outlined">{t('homeView.upgradeBannerFirstCTA', 'Change plan')}</Button>
		</RRLink>
	);

	const handleCloseProductCreatedModal = async (enablePayInStore: boolean) => {
		setShowProductCreatedModal(false);
		if (enablePayInStore) {
			const payStoreObject: ShopOnlinePaymentMethodObject = { id: 'PAY_STORE' };
			const updatedActivePaymentMethods = uniq([...activePaymentMethods, payStoreObject]);
			await api().shops.doc(shopId).public.update({
				activePaymentMethods: updatedActivePaymentMethods,
			});
		}
	};

	const handleVisitOnlineStoreClick = async () => {
		if (!activePaymentMethods.length) {
			const payStoreObject: ShopOnlinePaymentMethodObject = { id: 'PAY_STORE' };
			const updatedActivePaymentMethods = [payStoreObject];
			await api().shops.doc(shopId).public.update({
				activePaymentMethods: updatedActivePaymentMethods,
			});
		}
		const url = `${shopStoreUrl}/shop?admin=true`;
		newOnboardingClickEvent({ action: 'VISIT_ONLINE_STORE_MODAL' });
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	return (
		<>
			<Container maxWidth="md" className={classes.homeViewContainer}>
				{showTodaySection && <TodaySection amountOfBookings={amountOfBookingsToday} />}
				<OnboardingSection
					title={{
						text: !showTodaySection
							? t('homeView.welcomeToRentle', 'Welcome to Rentle!')
							: t('homeView.yourChecklist', 'Your checklist'),
						variant: !showTodaySection ? 'h4' : 'h6',
					}}
					subtitle={
						!showTodaySection
							? {
									text: t(
										'homeView.stepsToGetYouStarted',
										`We've gathered a few steps to get you started`,
									),
							  }
							: undefined
					}
				/>
				<Box>
					<Typography variant="h6">{t('homeView.learnMore', 'Learn more')}</Typography>
					{plan !== 'LITE' && (
						<HomeCTA
							img={KnowledgeIcon}
							imgAlt={'Guides'}
							title={t('homeView.blogBannerTitle', 'Suggested reading')}
							description={t(
								'homeView.blogBannerDescription',
								'We are here to help make your day-to-day business management easier. Check out some of our articles about various topics relevant for every business owner.',
							)}
							ctas={[
								{
									children: t('homeView.blogBannerCTA', 'Read more'),
									variant: 'outlined',
									LinkComponent: _ExternalLink,
									href: 'https://www.rentle.io/blog',
								},
							]}
						/>
					)}
					<HomeCTA
						img={SupportIcon}
						imgAlt={'Support icon'}
						title={t('homeView.helpBannerTitle', 'Got a question?')}
						description={t(
							'homeView.helpBannerText',
							'We are here to guide you in using Rentle so you can find answers to your questions.',
						)}
						ctas={[
							{
								children: t('homeView.helpBannerSecondCTA', 'Help center'),
								variant: 'outlined',
								LinkComponent: _ExternalLink,
								href: 'https://support.rentle.io',
							},
							{
								onClick: handleTalkToUsClick,
								children: t('homeView.helpBannerFirstCTA', 'Contact support'),
								variant: 'text',
								sx: { minWidth: 'max-content' },
							},
						]}
					/>
					{plan === 'LITE' && (
						<HomeCTA
							img={UpgradeIcon}
							imgAlt={'Upgrade icon'}
							title={t('homeView.upgradeBannerTitle', 'Grow better with Rentle Basic')}
							description={t(
								'homeView.upgradeBannerText',
								'Get access to more features by upgrading your account plan',
							)}
							externalLink={
								<ExternalLink href={'https://rentle.io/pricing'}>
									{t('homeView.upgradeBannerLink', 'View plans')}
								</ExternalLink>
							}
							ctas={[
								{
									LinkComponent: ChangePlanLink,
									href: Routes.PLANS,
								},
								{
									children: t('homeView.bookADemo', 'Book a demo'),
									variant: 'text',
									LinkComponent: _ExternalLink,
									href: 'https://www.rentle.io/demo',
								},
							]}
						/>
					)}
				</Box>
			</Container>
			<ProductCreatedModal
				open={showProductCreatedModal}
				onClose={(enablePayInStore) => handleCloseProductCreatedModal(enablePayInStore)}
				onConfirm={() => handleVisitOnlineStoreClick()}
				hasPaymentMethods={!!activePaymentMethods.length}
			/>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		homeViewContainer: {
			marginTop: theme.spacing(8),
			marginBottom: theme.spacing(2),
			paddingBottom: theme.spacing(8),
		},
	}),
);

export default Home;
